import { P, match } from 'ts-pattern';
import { PositionType } from '../__generated/model/index.js';
const ANGLE_EPSILON = 0.03;
const isAngleEqual = (angle1, angle2)=>{
    return Math.abs(angle1 - angle2) < ANGLE_EPSILON;
};
const isStraightAngle = (angle)=>isAngleEqual(angle, Math.PI);
const isRightAngle = (angle)=>{
    const modulo = angle % (Math.PI / 2);
    const angles = [
        Math.PI / 2,
        0
    ];
    return !isStraightAngle(angle) && angles.some((a)=>isAngleEqual(modulo, a));
};
const isAcuteAngle = (angle)=>angle < Math.PI / 2 || angle > 3 / 2 * Math.PI;
const isObtuseAngle = (angle)=>(angle > Math.PI / 2 || angle < 3 / 2 * Math.PI) && !isStraightAngle(angle);
export const getDeckPositionType = (connectedEntity, connection, side)=>{
    return match([
        connectedEntity,
        connection
    ]).returnType().with([
        undefined,
        P.any
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.LeftEnd;
        }).with('right', ()=>{
            return PositionType.RightEnd;
        }).exhaustive();
    }).with([
        {
            type: 'DeckSection',
            balustrade: false
        },
        P.any
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.LeftEnd;
        }).with('right', ()=>{
            return PositionType.RightEnd;
        }).exhaustive();
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return PositionType.FlatFlatRight;
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isAcuteAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatFlatAcuteLeft;
        }).with('right', ()=>{
            return PositionType.FlatFlatAcuteRight;
        }).exhaustive();
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isObtuseAngle)
        }
    ], ()=>{
        return PositionType.FlatFlatObtuse;
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return PositionType.FlatFlatObtuse;
    })// Stairs top left
    .with([
        {
            type: 'Stairs',
            balustradeLeft: false
        },
        {
            to: {
                type: 'rectangle-top-left'
            }
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to top left of stairs');
        }).with('right', ()=>{
            return PositionType.RightEnd;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            to: {
                type: 'rectangle-top-left'
            },
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to top left of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsStraightTopLeft;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            to: {
                type: 'rectangle-top-left'
            },
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to top left of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsCornerTopLeft;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            to: {
                type: 'rectangle-top-left'
            },
            angle: P.any
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to top left of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsOtherTopLeft;
        }).exhaustive();
    })// Stairs bottom left
    .with([
        {
            type: 'Stairs',
            balustradeLeft: false
        },
        {
            from: {
                type: 'rectangle-bottom-left'
            }
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.LeftEnd;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top left of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            from: {
                type: 'rectangle-bottom-left'
            },
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsStraightBottomLeft;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top left of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            from: {
                type: 'rectangle-bottom-left'
            },
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsCornerBottomLeft;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top left of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeLeft: true
        },
        {
            from: {
                type: 'rectangle-bottom-left'
            },
            angle: P.any
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsOtherBottomLeft;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top left of stairs');
        }).exhaustive();
    })// Stairs top right
    .with([
        {
            type: 'Stairs',
            balustradeRight: false
        },
        {
            from: {
                type: 'rectangle-top-right'
            }
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.LeftEnd;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top left of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            from: {
                type: 'rectangle-top-right'
            },
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsStraightTopRight;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top right of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            from: {
                type: 'rectangle-top-right'
            },
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsCornerTopRight;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top right of stairs');
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            from: {
                type: 'rectangle-top-right'
            },
            angle: P.any
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            return PositionType.FlatStairsOtherTopRight;
        }).with('right', ()=>{
            throw new Error('Right end of balustrade can not be connected to top right of stairs');
        }).exhaustive();
    })// Stairs bottom right
    .with([
        {
            type: 'Stairs',
            balustradeRight: false
        },
        {
            to: {
                type: 'rectangle-bottom-right'
            }
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to bottom right of stairs');
        }).with('right', ()=>{
            return PositionType.RightEnd;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            to: {
                type: 'rectangle-bottom-right'
            },
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to bottom right of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsStraightBottomRight;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            to: {
                type: 'rectangle-bottom-right'
            },
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to bottom right of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsCornerBottomRight;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs',
            balustradeRight: true
        },
        {
            to: {
                type: 'rectangle-bottom-right'
            },
            angle: P.any
        }
    ], ()=>{
        return match(side).with('left', ()=>{
            throw new Error('Left end of balustrade can not be connected to bottom right of stairs');
        }).with('right', ()=>{
            return PositionType.FlatStairsOtherBottomRight;
        }).exhaustive();
    }).otherwise(()=>{
        console.error(`Unknown geometry case encountered while getting deck position type! ${connectedEntity?.id}`, {
            connectedEntity,
            connection
        });
        throw new Error(`Unknown geometry case encountered while getting deck position type! ${connectedEntity?.id}`);
    });
};
export const getStairsPositionType = (connectedEntity, connection, corner)=>{
    return match([
        connectedEntity,
        connection
    ]).returnType().with([
        undefined,
        P.any
    ], ()=>{
        return match(corner).with('left-top', ()=>{
            return PositionType.StairsTopLeftEnd;
        }).with('right-top', ()=>{
            return PositionType.StairsTopRightEnd;
        }).with('left-bottom', ()=>{
            return PositionType.StairsBottomLeftEnd;
        }).with('right-bottom', ()=>{
            return PositionType.StairsBottomRightEnd;
        }).exhaustive();
    }).with([
        {
            type: 'DeckSection',
            balustrade: false
        },
        P.any
    ], ()=>{
        return match(corner).with('left-top', ()=>{
            return PositionType.StairsTopLeftEnd;
        }).with('right-top', ()=>{
            return PositionType.StairsTopRightEnd;
        }).with('left-bottom', ()=>{
            return PositionType.StairsBottomLeftEnd;
        }).with('right-bottom', ()=>{
            return PositionType.StairsBottomRightEnd;
        }).exhaustive();
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isRightAngle)
        }
    ], ()=>{
        return match(corner).with('left-top', ()=>{
            return PositionType.FlatStairsCornerTopLeft;
        }).with('right-top', ()=>{
            return PositionType.FlatStairsCornerTopRight;
        }).with('left-bottom', ()=>{
            return PositionType.FlatStairsCornerBottomLeft;
        }).with('right-bottom', ()=>{
            return PositionType.FlatStairsCornerBottomRight;
        }).exhaustive();
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.when(isStraightAngle)
        }
    ], ()=>{
        return match(corner).with('left-top', ()=>{
            return PositionType.FlatStairsStraightTopLeft;
        }).with('right-top', ()=>{
            return PositionType.FlatStairsStraightTopRight;
        }).with('left-bottom', ()=>{
            return PositionType.FlatStairsStraightBottomLeft;
        }).with('right-bottom', ()=>{
            return PositionType.FlatStairsStraightBottomRight;
        }).exhaustive();
    }).with([
        {
            balustrade: true,
            type: 'DeckSection'
        },
        {
            angle: P.any
        }
    ], ()=>{
        return match(corner).with('left-top', ()=>{
            return PositionType.StairsFlatOtherTopLeft;
        }).with('right-top', ()=>{
            return PositionType.StairsFlatOtherTopRight;
        }).with('left-bottom', ()=>{
            return PositionType.StairsFlatOtherBottomLeft;
        }).with('right-bottom', ()=>{
            return PositionType.StairsFlatOtherBottomRight;
        }).exhaustive();
    }).with([
        {
            type: 'Stairs'
        },
        P.any
    ], ()=>{
        return match(corner).with('left-top', ()=>PositionType.StairsTopLeftEnd).with('right-top', ()=>PositionType.StairsTopRightEnd).with('left-bottom', ()=>PositionType.StairsBottomLeftEnd).with('right-bottom', ()=>PositionType.StairsBottomRightEnd).exhaustive();
    }).otherwise(()=>{
        console.error(`Unknown geometry case encountered while getting stairs position type! ${connectedEntity?.id}`, {
            connectedEntity,
            connection,
            corner
        });
        throw new Error(`Unknown geometry case encountered while getting stairs position type! ${connectedEntity?.id}`);
    });
};

//# sourceMappingURL=position-type.js.map